import { Suspense, lazy, useEffect, useState } from 'react';
import { Navigate, useRoutes, useLocation } from 'react-router-dom';
// import { useGetConsultancySettingsQuery } from '../redux/slices/api';
// import { defaultsConfig } from '../config';
// layouts
import MainLayout from '../layouts/main';
import DashboardLayout from '../layouts/dashboard';
import LogoOnlyLayout from '../layouts/LogoOnlyLayout';
// guards
import GuestGuard from '../guards/GuestGuard';
import AuthGuard from '../guards/AuthGuard';
// import RoleBasedGuard from '../guards/RoleBasedGuard';
// components
import LoadingScreen from '../components/LoadingScreen';
import useAuth from '../hooks/useAuth';
import { PATH_DASHBOARD } from './paths';

// ----------------------------------------------------------------------

const Loadable = (Component) => (props) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { pathname } = useLocation();
  const isDashboard = pathname.includes('/dashboard');

  return (
    <Suspense
      fallback={
        <LoadingScreen
          sx={{
            ...(!isDashboard && {
              top: 0,
              left: 0,
              width: 1,
              zIndex: 9999,
              position: 'fixed',
            }),
          }}
        />
      }
    >
      <Component {...props} />
    </Suspense>
  );
};

export default function Router() {
  const { user, isAuthenticated } = useAuth();
  const [initialDashboard, setInitialDashboard] = useState('');
  // const { data: consultancySettings } = useGetConsultancySettingsQuery();

  useEffect(() => {
    if (user && user.email) {
      // if (consultancySettings) Object.assign(defaultsConfig, consultancySettings);
      if (!user.role) {
        setInitialDashboard(PATH_DASHBOARD.yawn.architectDashboard);
      } else if (user.company_type) {
        if (user.company_type === 'manufacturer' && user.company_plan) {
          setInitialDashboard(PATH_DASHBOARD.yawn.root);
        }
        if (user.company_type === 'manufacturer' && !user.company_plan) {
          setInitialDashboard(PATH_DASHBOARD.yawn.product.root);
        } else {
          setInitialDashboard(PATH_DASHBOARD.yawn.architectDashboard);
        }
      }
    }
  }, [user]);

  return useRoutes([
    {
      path: 'auth',
      children: [
        {
          path: 'login',
          element: (
            <GuestGuard>
              <Login />
            </GuestGuard>
          ),
        },
        {
          path: 'register',
          element: (
            <GuestGuard>
              <Register />
            </GuestGuard>
          ),
        },
        { path: 'login-unprotected', element: <Login /> },
        { path: 'register-unprotected', element: <Register /> },
        { path: 'reset-password', element: <ResetPassword /> },
        { path: 'verify', element: <VerifyCode /> },
      ],
    },

    // Dashboard Routes
    {
      path: 'dashboard',
      element: (
        <AuthGuard>
          <DashboardLayout />
        </AuthGuard>
      ),
      children: [
        { path: '', element: <Navigate to={initialDashboard} replace /> },
        {
          path: 'yawn',
          children: [
            { path: '', element: <Navigate to={initialDashboard} replace /> },
            { path: 'architect-dashboard', element: <ArchitectDashboard /> },
            { path: 'manufacturer-dashboard', element: <ManufacturerDashboard /> },
            { path: 'analytics-dashboard', element: <AnalyticsDashboard /> },
            {
              path: 'project',
              children: [
                { path: '', element: <Navigate to="/dashboard/yawn/project/list" replace /> },
                { path: 'list', element: <Projects /> },
                { path: 'new', element: <Project /> },
                { path: ':id/edit', element: <Project /> },
              ],
            },
            {
              path: 'schedule',
              children: [
                { path: '', element: <Navigate to="/dashboard/yawn/schedule/list" replace /> },
                { path: 'list', element: <Schedules /> },
                { path: 'new', element: <Schedule /> },
                { path: ':id/edit', element: <Schedule /> },
              ],
            },
            ...(import.meta.env.VITE_APP_ENV_NAME !== 'prod'
              ? [
                  {
                    path: 'spec',
                    children: [
                      { path: '', element: <Navigate to="/dashboard/yawn/spec/list" replace /> },
                      { path: 'list', element: <Specs /> },
                      { path: 'new', element: <Spec /> },
                      { path: ':id/edit', element: <Spec /> },
                    ],
                  },
                ]
              : []),
            {
              path: 'product',
              children: [
                { path: '', element: <Navigate to="/dashboard/yawn/product/list" replace /> },
                { path: 'list', element: <Products /> },
                { path: ':id/list', element: <Products /> },
                { path: 'new', element: <Product /> },
                { path: ':id/edit', element: <Product /> },
              ],
            },
            {
              path: 'company',
              children: [
                { path: '', element: <Navigate to="/dashboard/yawn/company/list" replace /> },
                { path: 'list', element: <Companies /> },
                { path: 'new', element: <Company /> },
                { path: ':id/edit', element: <Company /> },
              ],
            },
            {
              path: 'user',
              children: [
                { path: '', element: <Navigate to="/dashboard/yawn/user/list" replace /> },
                { path: 'list', element: <UserList /> },
                { path: 'new', element: <UserCreate /> },
                { path: ':id/edit', element: <UserProfile /> },
              ],
            },
            {
              path: 'constructionProject',
              children: [
                {
                  path: '',
                  element: <Navigate to="/dashboard/yawn/constructionProject/all" replace />,
                },
                { path: 'label/:customLabel', element: <ConstructionProject /> },
                {
                  path: 'label/:customLabel/:constructionProjectId',
                  element: <ConstructionProject />,
                },
                { path: ':systemLabel', element: <ConstructionProject /> },
                {
                  path: ':systemLabel/:constructionProjectId',
                  element: <ConstructionProject />,
                },
                {
                  path: ':systemLabel/:constructionProjectId/edit',
                  element: <ConstructionProject isEdit />,
                },
              ],
            },
            {
              path: 'invitation',
              children: [{ path: ':id', element: <Invitation /> }],
            },
            {
              path: 'admin',
              children: [
                { path: '', element: <Navigate to="/dashboard/admin/productMapping" replace /> },
                { path: 'productMapping', element: <ProductMapping /> },
                { path: 'stats', element: <Stats /> },
                { path: 'standardLink', element: <StandardLink /> },
              ],
            },
          ],
        },
        {
          path: 'user',
          children: [
            { path: '', element: <Navigate to="/dashboard/user/profile" replace /> },
            { path: 'profile', element: <UserProfile /> },
            { path: 'list', element: <UserList /> },
            { path: 'new', element: <UserCreate /> },
            { path: 'edit', element: <UserCreate /> },
            { path: ':id/edit', element: <UserProfile /> },
            { path: 'account', element: <UserAccount /> },
            { path: ':id/account', element: <UserAccount /> },
          ],
        },
        { path: 'calendar', element: <Calendar /> },
        {
          path: 'settings',
          children: [
            { path: '', element: <Navigate to="/dashboard/user/account" replace /> },
            { path: 'display', element: <DisplaySettings /> },
          ],
        },
      ],
    },
    // Main Routes
    {
      path: '*',
      element: <LogoOnlyLayout />,
      children: [
        { path: '500', element: <Page500 /> },
        { path: '404', element: <NotFound /> },
        { path: '*', element: <Navigate to="/404" replace /> },
      ],
    },
    {
      path: '',
      element: <MainLayout />,
      children: [{ path: '', element: <LandingPage /> }],
    },
    { path: '*', element: <Navigate to="/404" replace /> },
  ]);
}
// Authentication
const Login = Loadable(lazy(() => import('../pages/authentication/Login')));
const Register = Loadable(lazy(() => import('../pages/authentication/Register')));
const ResetPassword = Loadable(lazy(() => import('../pages/authentication/ResetPassword')));
const VerifyCode = Loadable(lazy(() => import('../pages/authentication/VerifyCode')));
// Dashboard
const UserProfile = Loadable(lazy(() => import('../pages/dashboard/UserProfile')));
const UserList = Loadable(lazy(() => import('../pages/dashboard/UserList')));
const UserAccount = Loadable(lazy(() => import('../pages/dashboard/UserAccount')));
const UserCreate = Loadable(lazy(() => import('../pages/dashboard/UserCreate')));
const Calendar = Loadable(lazy(() => import('../pages/dashboard/Calendar')));
// Main
const LandingPage = Loadable(lazy(() => import('../pages/LandingPage')));
const Page500 = Loadable(lazy(() => import('../pages/Page500')));
const NotFound = Loadable(lazy(() => import('../pages/Page404')));
// yawn
const ArchitectDashboard = Loadable(lazy(() => import('../pages/dashboard/ArchitectDashboard')));
const ManufacturerDashboard = Loadable(
  lazy(() => import('../pages/dashboard/ManufacturerDashboard'))
);
const AnalyticsDashboard = Loadable(lazy(() => import('../pages/dashboard/AnalyticsDashboard')));
const Projects = Loadable(lazy(() => import('../pages/dashboard/Projects')));
const Project = Loadable(lazy(() => import('../pages/dashboard/Project')));
const Schedules = Loadable(lazy(() => import('../pages/dashboard/Schedules')));
const Schedule = Loadable(lazy(() => import('../pages/dashboard/Schedule')));
const Specs = Loadable(lazy(() => import('../pages/dashboard/Specs')));
const Spec = Loadable(lazy(() => import('../pages/dashboard/Spec')));
const Products = Loadable(lazy(() => import('../pages/dashboard/Products')));
const Product = Loadable(lazy(() => import('../pages/dashboard/Product')));
const Company = Loadable(lazy(() => import('../pages/dashboard/Company')));
const Companies = Loadable(lazy(() => import('../pages/dashboard/Companies')));
const ConstructionProject = Loadable(lazy(() => import('../pages/dashboard/ConstructionProject')));
const Invitation = Loadable(lazy(() => import('../pages/dashboard/InvitationApprove')));
const ProductMapping = Loadable(lazy(() => import('../pages/dashboard/ProductMapping')));
const Stats = Loadable(lazy(() => import('../pages/dashboard/Stats')));
const StandardLink = Loadable(lazy(() => import('../pages/dashboard/StandardLink')));
// settings
const DisplaySettings = Loadable(lazy(() => import('../components/settings')));
